import { ResponseError } from '@tourfold/libraries/tf-api-client-public-v2';

/**
 * Parsed RFC 9457 problem detail. The frontend should switch on `type` (the problem URI) and the
 * structured `data` map (pointers, reason codes, sibling ids) to render translated, situation-
 * specific error messages — `detail` is an English fallback only.
 */
export interface ParsedProblem {
  /** Best human-readable English string the server provided (`detail` or `title`); empty if neither was present. */
  readonly detail: string;
  /** RFC 9457 `type` URI — e.g. `document-management/name-taken`. */
  readonly type?: string;
  /** HTTP status code if the error came from a Response. */
  readonly status?: number;
  /** Sub-problems list (used by upload-*-failed to convey filestore detail). */
  readonly errors?: readonly Record<string, unknown>[];
  /** Structured discriminator data: `{name}`, `{reason}`, `{cause}`, `{existingId}`, `{parentId}`, etc. */
  readonly data: Readonly<Record<string, unknown>>;
}

/**
 * Reads the RFC 9457 problem body off a generated-client error. Returns the parsed shape so the
 * caller can switch on `type` + `data.reason`/`data.cause` to render a translated, contextual
 * inline message.
 *
 * Returns null when the error is not an HTTP response error (e.g. network failure) — caller should
 * fall back to a generic message in that case.
 */
export async function parseProblemFromError(err: unknown): Promise<ParsedProblem | null> {
  if (!(err instanceof ResponseError)) return null;
  const status = err.response.status;
  let body: unknown = null;
  try {
    body = await err.response.clone().json();
  } catch {
    return { detail: '', status, data: {} };
  }
  if (!body || typeof body !== 'object') {
    return { detail: '', status, data: {} };
  }
  const b = body as Record<string, unknown>;
  const detail = typeof b['detail'] === 'string' ? b['detail'].trim() : '';
  const title = typeof b['title'] === 'string' ? b['title'].trim() : '';
  const type = typeof b['type'] === 'string' ? b['type'] : undefined;
  const errors = Array.isArray(b['errors'])
    ? (b['errors'] as unknown[]).filter((e): e is Record<string, unknown> =>
        e != null && typeof e === 'object',
      )
    : undefined;
  const data =
    b['data'] != null && typeof b['data'] === 'object'
      ? (b['data'] as Record<string, unknown>)
      : {};
  return { detail: detail || title, type, status, errors, data };
}
